import { useEffect } from "react";
import onlineStatusManager from "../utils/onlineStatusManager";

/**
 * React hook to manage user online status via backend API
 *
 * @param {string|number} userId - Current user ID (null if not logged in)
 * @param {string} accessToken - JWT access token for API authentication
 * @param {boolean} enabled - Whether to enable presence tracking (default: true)
 *
 * @example
 * // In your main App component or after login:
 * const { currentUser } = useContext(UserContext);
 * const token = localStorage.getItem('token');
 * useOnlineStatus(currentUser?.id, token);
 *
 * @example
 * // Disable presence tracking:
 * useOnlineStatus(userId, token, false);
 */
export const useOnlineStatus = (userId, accessToken, enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      console.log("[PRESENCE_HOOK] Presence tracking disabled");
      return;
    }

    if (userId && accessToken) {
      // Keyed shape, not a standalone argument: a bare id has no key for the
      // breadcrumb scrubber to match on (#528). This call is byte-identical
      // to the logout branch below ON PURPOSE — the prose used to differ
      // ("updating" vs "clearing"), which let a Sentry breadcrumb tell login
      // from logout even though neither said a direction word. Locally the
      // { userId } value (an id here, null below) still says which way; the
      // scrubber redacts it by key, so the filtered pair cannot be told
      // apart. The pair test in sentryBeforeBreadcrumb.test.js pins both
      // the literal and the argument shape.
      console.log("[PRESENCE_HOOK] Session changed, syncing presence state:", {
        userId: userId ?? null,
      });
      onlineStatusManager.setUserOnline(userId, accessToken);
    } else if (userId && !accessToken) {
      console.warn(
        "[PRESENCE_HOOK] User logged in but no access token provided"
      );
    } else {
      // Byte-identical to the login branch above — see the comment there.
      console.log("[PRESENCE_HOOK] Session changed, syncing presence state:", {
        userId: userId ?? null,
      });
      onlineStatusManager.setUserOffline();
    }
  }, [userId, accessToken, enabled]);

  return {
    refreshStatus: () => onlineStatusManager.refreshOnlineStatus(),
    setOffline: () => onlineStatusManager.setUserOffline(),
  };
};

export default useOnlineStatus;
